/**
 * Adzuki
 * maxx_intelligence.js
 *
 * Controller for the redesigned Maxx Intelligence panel (Quick Actions Tools tab).
 * The /Ai/maxxIntelligenceStream endpoint is a plain JSON POST — the server
 * returns the complete assistant reply once OpenAI finishes. We simulate the
 * token-by-token streaming UX on the client by revealing the text in small
 * chunks on a timer. Real SSE was tried but the dev lighttpd pipeline eats
 * chunked streaming responses when brotli is in the Accept-Encoding mix.
 *
 * Talks to:
 *   POST /Ai/maxxIntelligenceStream              (JSON POST → JSON reply)
 *   GET  /Ai/maxxIntelligenceConversations[/q:<term>]  (drawer history list; search is a path segment, not ?q=)
 *   GET  /Ai/maxxIntelligenceConversation/:id    (messages for one thread)
 *   POST /Ai/maxxIntelligenceDeleteConversation/:id
 *   POST /Ai/maxxIntelligenceRenameConversation/:id  (body: title)
 *   POST /Ai/saveSnippet                         (snippet: <text>)
 *   POST /Ai/sendFeedback                        (session_id, question, response, endpoint, explanation)
 */
(function maxxIntelligenceModule($, Adzuki) {
	'use strict';

	let SVG = {
		sparkle  : '<img src="/adzuki/images/tinymce-maxx-gpt.png" class="mi-avatar-icon" alt="" />',
		copy     : '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>',
		save     : '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>',
		refresh  : '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>',
		thumbUp  : '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><line x1="7" y1="22" x2="7" y2="11"/></svg>',
		thumbDown: '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3H10z"/><line x1="17" y1="2" x2="17" y2="13"/></svg>',
		trash    : '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>',
		insert   : '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>',
		fileDoc  : '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>',
		sendIcon : '<svg data-mi-send-icon viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12l14-7-5 16-3-7-6-2z"/></svg>',
		stopIcon : '<svg data-mi-send-icon viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>'
	};

	function uid() {
		return Math.random().toString(36).slice(2, 10);
	}

	function escapeHtml(s) {
		let safeValue = (s === null || s === undefined) ? '' : s;
		return String(safeValue).replace(/[&<>"']/g, function escapeCharacter(ch) {
			return { '&': '&amp;',
					'<': '&lt;',
					'>': '&gt;',
					'"': '&quot;',
					"'": '&#39;' }[ch];
		});
	}

	/**
	 * Allow only http(s), mailto, and same-origin-relative hrefs after markdown
	 * link conversion. Rejects javascript: and other schemes.
	 */
	function sanitizeMarkdownHref(href) {
		let trimmed = String(href || '').trim();
		if (!trimmed) {
			return '';
		}
		if ((/^(https?:|mailto:)/i).test(trimmed) || trimmed.charAt(0) === '/') {
			return trimmed;
		}
		return '';
	}

	/**
	 * Convert a subset of markdown (bold, italic, links, headings, code,
	 * strikethrough, simple tables) into HTML. Content is HTML-escaped first
	 * so model output cannot inject tags. Newlines are left intact — the
	 * message bubble uses white-space: pre-wrap.
	 */
	function formatAssistantMarkdown(text) {
		let html = escapeHtml(text);

		// Fenced code blocks — run before inline code / emphasis.
		html = html.replace(/```([^\n`]*)\n?([\s\S]*?)```/g, function formatCodeBlock(match, lang, code) {
			let langClass = lang ? (' data-mi-lang="' + lang.trim() + '"') : '';
			return '<pre class="mi-md-pre"' + langClass + '><code>' + code.replace(/\n$/, '') + '</code></pre>';
		});

		// Inline code
		html = html.replace(/`([^`\n]+)`/g, '<code class="mi-md-code">$1</code>');

		// Simple markdown tables (header + separator + rows)
		html = formatMarkdownTables(html);

		// Links: [label](url) — href sanitized against javascript: etc.
		html = html.replace(/\[([^\]]+)\]\(([^)\n]+)\)/g, function formatMarkdownLink(match, label, href) {
			let safeHref = sanitizeMarkdownHref(href);
			if (!safeHref) {
				return match;
			}
			return '<a href="' + safeHref + '" target="_blank" rel="noopener noreferrer">' + label + '</a>';
		});

		// Headings (# … ######) → strong line (keeps bubble typography simple)
		html = html.replace(/^#{1,6}\s+(.+)$/gm, '<strong class="mi-md-heading">$1</strong>');

		// Bold then italic (order matters so ** wins over *)
		html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
		html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');
		html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
		html = html.replace(/(^|[\s(])_([^_\n]+)_([\s).,!?:;]|$)/g, '$1<em>$2</em>$3');

		// Strikethrough
		html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');

		return html;
	}

	function isMarkdownTableRow(line) {
		return (/^\s*\|.*\|\s*$/).test(line);
	}

	function isMarkdownTableSeparator(line) {
		return (/^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/).test(line);
	}

	function parseMarkdownTableRow(line) {
		let trimmed = String(line).trim();
		if (trimmed.charAt(0) === '|') {
			trimmed = trimmed.substring(1);
		}
		if (trimmed.charAt(trimmed.length - 1) === '|') {
			trimmed = trimmed.substring(0, trimmed.length - 1);
		}
		return trimmed.split('|').map(function trimCell(cell) {
			return cell.trim();
		});
	}

	function convertMarkdownTableToHtml(tableLines) {
		let headerCells = parseMarkdownTableRow(tableLines[0]);
		let bodyLines   = tableLines.slice(2);
		let html        = '<div class="mi-md-table-wrap"><table class="mi-md-table"><thead><tr>';
		let i;
		let j;
		let cells;

		for (i = 0; i < headerCells.length; i++) {
			html += '<th>' + headerCells[i] + '</th>';
		}
		html += '</tr></thead><tbody>';

		for (i = 0; i < bodyLines.length; i++) {
			cells = parseMarkdownTableRow(bodyLines[i]);
			html += '<tr>';
			for (j = 0; j < cells.length; j++) {
				html += '<td>' + cells[j] + '</td>';
			}
			html += '</tr>';
		}

		html += '</tbody></table></div>';
		return html;
	}

	function formatMarkdownTables(text) {
		let lines      = String(text).split(/\r\n|\r|\n/);
		let output     = [];
		let tableLines = [];
		let i          = 0;

		while (i < lines.length) {
			if (
				isMarkdownTableRow(lines[i])
				&& i + 1 < lines.length
				&& isMarkdownTableSeparator(lines[i + 1])
			) {
				tableLines = [lines[i], lines[i + 1]];
				i += 2;
				while (i < lines.length && isMarkdownTableRow(lines[i])) {
					tableLines.push(lines[i]);
					i += 1;
				}
				output.push(convertMarkdownTableToHtml(tableLines));
			} else {
				output.push(lines[i]);
				i += 1;
			}
		}

		return output.join('\n');
	}

	function renderMessageContent(role, content, streaming) {
		if (role === 'assistant') {
			return formatAssistantMarkdown(content) + (streaming ? '<span class="mi-cursor"></span>' : '');
		}
		return escapeHtml(content) + (streaming ? '<span class="mi-cursor"></span>' : '');
	}

	/**
	 * Wrap each case-insensitive occurrence of `query` in `plain` with
	 * <strong class="mi-history-match">; everything else is HTML-escaped.
	 */
	function highlightSearchInPlainText(plain, query) {
		let text = (plain === null || plain === undefined) ? '' : String(plain);
		let q = (query || '').trim();
		if (!q) {
			return escapeHtml(text);
		}
		let lowerText = text.toLowerCase();
		let lowerQ = q.toLowerCase();
		let needleLen = q.length;
		let pos = 0;
		let out = '';
		while (pos < text.length) {
			let hit = lowerText.indexOf(lowerQ, pos);
			if (hit < 0) {
				out += escapeHtml(text.slice(pos));
				break;
			}
			out += escapeHtml(text.slice(pos, hit));
			out += '<strong class="mi-history-match">' + escapeHtml(text.slice(hit, hit + needleLen)) + '</strong>';
			pos = hit + needleLen;
		}
		return out;
	}

	function formatWhen(ts) {
		if (!ts) { return ''; }
		// Backend returns MySQL DATETIME (e.g. "2026-04-20 14:32:11") — Safari rejects the space.
		let normalized = (typeof ts === 'string') ? ts.replace(' ', 'T') : ts;
		let d          = new Date(normalized);
		if (isNaN(d.getTime())) { return ''; }
		let now = new Date();
		if (d.toDateString() === now.toDateString()) {
			return d.toLocaleTimeString([], { hour  : 'numeric',
minute: '2-digit' });
		}
		let y = new Date(now);
		y.setDate(y.getDate() - 1);
		if (d.toDateString() === y.toDateString()) { return 'Yesterday'; }
		return d.toLocaleDateString([], { month: 'short',
day  : 'numeric' });
	}

	function groupConvos(convos) {
		let now       = new Date();
		let today     = now.toDateString();
		let yesterday = new Date(now);
		yesterday.setDate(yesterday.getDate() - 1);
		yesterday = yesterday.toDateString();
		let last7 = new Date(now);
		last7.setDate(last7.getDate() - 7);

		let groups = { Today            : [],
Yesterday        : [],
'Previous 7 days': [],
Older            : [] };

		for (let i = 0; i < convos.length; i++) {
			let c  = convos[i];
			let ts = c.last_message_at || c.modified || c.created;
			let d  = new Date((typeof ts === 'string') ? ts.replace(' ', 'T') : ts);

			if (isNaN(d.getTime())) {
				groups.Older.push(c);
			} else if (d.toDateString() === today) {
				groups.Today.push(c);
			} else if (d.toDateString() === yesterday) {
				groups.Yesterday.push(c);
			} else if (d >= last7) {
				groups['Previous 7 days'].push(c);
			} else {
				groups.Older.push(c);
			}
		}

		return groups;
	}

	function init(opts) {
		let options = opts || {};
		let root = options.root;

		if (!root) {
			throw new Error('Adzuki.MaxxIntelligence.init: `root` is required');
		}

		// Optional: when provided, an "Insert Content" per-message action
		// appears on assistant replies. The TinyMCE editor wires this to
		// inject the reply into the currently-focused editor instance.
		let onInsertContent = (typeof options.onInsertContent === 'function') ? options.onInsertContent : null;

		function url(path) {
			if (Adzuki && typeof Adzuki.get_url === 'function') {
				return Adzuki.get_url(path);
			}
			return path;
		}

		function $q(sel) { return root.querySelector(sel); }

		let emptyEl        = $q('[data-mi-empty]');
		let messagesEl     = $q('[data-mi-messages]');
		let scrollEl       = $q('[data-mi-scroll]');
		let inputEl        = $q('[data-mi-input]');
		let sendBtn        = $q('[data-mi-send]');
		let micBtn         = $q('[data-mi-mic]');
		let attachBtn      = $q('[data-mi-attach]');
		let fileInputEl    = $q('[data-mi-file-input]');
		let attachmentsEl  = $q('[data-mi-attachments]');
		let drawer         = $q('[data-mi-drawer]');
		let backdrop       = $q('[data-mi-backdrop]');
		let historyEl      = $q('[data-mi-history]');
		let searchEl       = $q('[data-mi-search]');
		let toastEl        = $q('[data-mi-toast]');
		let toastText      = $q('[data-mi-toast-text]');
		let clearBtn       = root.querySelector('[data-mi-action="clear"]');
		let brandIcon  = root.querySelector('.mi-brand-icon');

		// State
		let activeConversationId = null;
		let messages             = []; // [{ id, role, content, streaming?, log_id?, attachments? }]
		let conversations        = []; // cached drawer list
		let abortCtrl            = null;
		let generating           = false;
		let searchQuery          = '';
		let toastTimer           = null;
		// Pending attachments — uploaded but not yet sent. Shape: [{ id, filename, mime_type, thumb_url, uploading, error, temp_id }]
		let pendingAttachments   = [];
		let uploadCounter        = 0;
		let speechRecognition    = null;
		let listening            = false;
		let voicePrefix          = '';
		let voiceCommitted       = '';
		let assistantAvatarHtml  = SVG.sparkle;
		let userAvatarHtml       = '<div class="mi-msg-avatar is-user">You</div>';

		// Keep JS-rendered assistant avatars using the exact same icon URL as the view.
		if (brandIcon && brandIcon.getAttribute('src')) {
			let brandIconSrc = brandIcon.getAttribute('src').replace(/"/g, '&quot;');
			assistantAvatarHtml = '<img src="' + brandIconSrc + '" class="mi-avatar-icon" alt="" />';
		}

		// Mirror the agent photo used in the header (.photo) for user chat bubbles.
		let headerPhotoUrl = $('.photo').first().css('background-image');
		if (headerPhotoUrl && headerPhotoUrl !== 'none') {
			let safePhotoUrl = String(headerPhotoUrl).replace(/'/g, '&#39;');
			userAvatarHtml
				= '<div class="mi-msg-avatar is-user mi-user-avatar-photo" style=\'background-image: ' + safePhotoUrl + ';\'></div>';
		}

		// ------------------------------------------------------------------
		// Rendering
		// ------------------------------------------------------------------

		function renderMessages() {
			if (!messages.length) {
				emptyEl.hidden         = false;
				messagesEl.hidden      = true;
				messagesEl.innerHTML   = '';
				updateClearBtn();
				return;
			}

			emptyEl.hidden    = true;
			messagesEl.hidden = false;

			let html = '';

			for (let i = 0; i < messages.length; i++) {
				html += renderMessage(messages[i]);
			}

			messagesEl.innerHTML = html;
			addTableExports();

			requestAnimationFrame(function scrollMessagesToBottom() {
				scrollEl.scrollTop = scrollEl.scrollHeight;
			});

			updateClearBtn();
		}

		// MAXX-174: an Export CSV button under each table in a finished answer.
		// renderMessages() rebuilds every message, so this runs after each
		// rebuild; the answer still being revealed is skipped until it is done.
		function addTableExports() {
			if (!Adzuki.TableExport) {
				return;
			}

			for (let i = 0; i < messages.length; i++) {
				let m = messages[i];
				if (m.role !== 'assistant' || m.streaming) {
					continue;
				}

				let node = messagesEl.querySelector('[data-mi-msg-id="' + m.id + '"] [data-mi-msg-content]');
				if (node) {
					Adzuki.TableExport.enhance(node, {
						prefix : 'maxx-intelligence',
						wrapper: '.mi-md-table-wrap',
					});
				}
			}
		}

		function renderMessage(m) {
			let isUser    = m.role === 'user';
			let avatar    = isUser
				? userAvatarHtml
				: '<div class="mi-msg-avatar is-assistant">' + assistantAvatarHtml + '</div>';
			let author    = isUser ? 'You' : 'Maxx Intelligence';
			let contentHtml = renderMessageContent(m.role, m.content, Boolean(m.streaming));
			let actions   = '';

			// Rendered attachments for past user turns (history) or the turn
			// that was just sent. Image attachments show a thumbnail; document
			// attachments show a file-type tile (clickable to open the raw file).
			let attachmentsHtml = '';
			if (isUser && m.attachments && m.attachments.length) {
				let tiles = '';
				for (let i = 0; i < m.attachments.length; i++) {
					let a    = m.attachments[i];
					let name = escapeHtml(a.filename || '');
					let href = url('/Ai/maxxIntelligenceAttachmentImage/' + encodeURIComponent(a.id));

					if (isImageMime(a.mime_type)) {
						let src = (a && a.thumb_url) ? a.thumb_url : href;
						tiles += '<a class="mi-msg-attachment" href="' + href + '" target="_blank" rel="noopener noreferrer"'
							+ ' title="' + name + '">'
							+ '<img src="' + src + '" alt="' + name + '"/>'
							+ '</a>';
					} else {
						let label = fileKindLabel(a.mime_type, a.filename);
						tiles += '<a class="mi-msg-attachment mi-msg-attachment-doc" href="' + href + '" target="_blank" rel="noopener noreferrer"'
							+ ' title="' + name + '">'
							+ '<span class="mi-msg-attachment-icon">' + SVG.fileDoc + '</span>'
							+ '<span class="mi-msg-attachment-label">' + label + '</span>'
							+ '<span class="mi-msg-attachment-name">' + name + '</span>'
							+ '</a>';
					}
				}
				attachmentsHtml = '<div class="mi-msg-attachments">' + tiles + '</div>';
			}

			if (!isUser && !m.streaming && m.content) {
				let insertBtn = '';
				if (typeof onInsertContent === 'function') {
					insertBtn
						= '<button type="button" class="mi-mini-btn mi-mini-btn-primary" data-mi-msg-action="insert" data-mi-msg-id="' + m.id + '">'
							+ SVG.insert + ' Insert Content'
						+ '</button>';
				}

				// Feedback thumbs are intentionally hidden for now.
				actions
					= '<div class="mi-msg-actions">'
						+ insertBtn
						+ '<button type="button" class="mi-mini-btn" data-mi-msg-action="copy"       data-mi-msg-id="' + m.id + '">' + SVG.copy + ' Copy</button>'
						+ '<button type="button" class="mi-mini-btn" data-mi-msg-action="save"       data-mi-msg-id="' + m.id + '">' + SVG.save + ' Save to Snippets</button>'
						+ '<button type="button" class="mi-mini-btn" data-mi-msg-action="regenerate" data-mi-msg-id="' + m.id + '">' + SVG.refresh + ' Regenerate</button>'
					+ '</div>';
			}

			return '<div class="mi-msg-row" data-mi-msg-id="' + m.id + '">'
					+ avatar
					+ '<div class="mi-msg-body">'
						+ '<div class="mi-msg-author">' + author + '</div>'
						+ attachmentsHtml
						+ '<div class="mi-msg-content" data-mi-msg-content>' + contentHtml + '</div>'
						+ actions
					+ '</div>'
				+ '</div>';
		}

		function updateStreamingMessage(msgId, text) {
			let node = messagesEl.querySelector('[data-mi-msg-id="' + msgId + '"] [data-mi-msg-content]');
			if (!node) { return; }
			node.innerHTML     = renderMessageContent('assistant', text, true);
			scrollEl.scrollTop = scrollEl.scrollHeight;
		}

		function renderHistory() {
			// List is already scoped by GET /Ai/maxxIntelligenceConversations/q:…
			// (server matches title and message bodies). Do not re-filter here by
			// title only — that hid every match whose body matched but title did not.
			let filtered = conversations;

			if (!filtered.length) {
				let emptyLabel = searchQuery.trim()
					? 'No conversations match your search.'
					: 'No conversations yet.';
				historyEl.innerHTML = '<div class="mi-history-empty">' + escapeHtml(emptyLabel) + '</div>';
				return;
			}

			let groups = groupConvos(filtered);
			let html   = '';

			Object.keys(groups).forEach(function renderHistoryGroup(label) {
				let items = groups[label];
				if (!items.length) { return; }

				html += '<div class="mi-history-group">' + label + '</div>';

				for (let i = 0; i < items.length; i++) {
					let c               = items[i];
					let active          = (Number(c.id) === Number(activeConversationId)) ? ' is-active' : '';
					let when            = formatWhen(c.last_message_at || c.modified || c.created);
					let hasMessageCount = c.message_count !== null && c.message_count !== undefined;
					let meta            = when + (hasMessageCount ? ' · ' + c.message_count + ' msg' + (c.message_count === 1 ? '' : 's') : '');
					let titleHtml       = searchQuery.trim()
						? highlightSearchInPlainText(c.title || 'Untitled', searchQuery)
						: escapeHtml(c.title || 'Untitled');

					html
						+= '<div class="mi-history-item' + active + '" data-mi-convo-id="' + c.id + '">'
							+ '<div class="mi-history-title">' + titleHtml + '</div>'
							+ '<div class="mi-history-meta">' + escapeHtml(meta) + '</div>'
							+ '<button class="mi-history-delete" data-mi-delete-id="' + c.id + '" title="Delete">' + SVG.trash + '</button>'
						+ '</div>';
				}
			});

			historyEl.innerHTML = html;
		}

		function updateClearBtn() {
			if (!clearBtn) { return; }
			clearBtn.hidden = !activeConversationId;
		}

		function setSendMode(mode /* 'send' | 'stop' | 'disabled' */) {
			if (mode === 'stop') {
				sendBtn.disabled = false;
				sendBtn.innerHTML = SVG.stopIcon;
				updateMicButton();
				return;
			}

			let hasText     = inputEl.value.trim().length > 0;
			let hasReady    = pendingAttachments.some(function isReady(a) { return a.id && !a.uploading; });
			let anyUploading = pendingAttachments.some(function isUploading(a) { return a.uploading; });
			sendBtn.disabled  = (!hasText && !hasReady) || anyUploading || mode === 'disabled';
			sendBtn.innerHTML = SVG.sendIcon;
			updateMicButton();
		}

		function getSpeechRecognitionCtor() {
			return window.SpeechRecognition || window.webkitSpeechRecognition || null;
		}

		function resetMicIcon() {
			if (!micBtn) { return; }
			micBtn.innerHTML = '<i class="fas fa-microphone" aria-hidden="true"></i>';
		}

		function updateMicButton() {
			if (!micBtn) { return; }
			micBtn.disabled = generating;
			micBtn.setAttribute('aria-pressed', listening ? 'true' : 'false');
			if (listening) {
				micBtn.classList.add('is-listening');
				micBtn.title = 'Stop listening';
			} else {
				micBtn.classList.remove('is-listening');
				micBtn.title = 'Voice input';
			}
		}

		function buildVoiceInputValue(interim) {
			let parts = [];
			if (voicePrefix) { parts.push(voicePrefix); }
			if (voiceCommitted) { parts.push(voiceCommitted); }
			if (interim) { parts.push(interim); }
			return parts.join(' ').trim();
		}

		function stopVoiceInput() {
			if (!listening && !speechRecognition) { return; }

			listening = false;

			if (speechRecognition) {
				try {
					speechRecognition.stop();
				} catch (stopErr) {
					// Browser may already have ended the session.
				}
				try {
					speechRecognition.abort();
				} catch (abortErr) {
					// Browser may already have ended the session.
				}
				speechRecognition = null;
			}

			resetMicIcon();
			updateMicButton();
		}

		function startVoiceInput() {
			if (!micBtn || generating || listening) { return; }

			let SpeechRecognitionCtor = getSpeechRecognitionCtor();
			if (!SpeechRecognitionCtor) {
				showToast('Voice input is not supported in this browser');
				if (Adzuki && typeof Adzuki.StatusMessage === 'function') {
					Adzuki.StatusMessage(
						'error',
						'Voice input is not supported in this browser. Chrome is recommended.',
						'Voice Input'
					);
				}
				return;
			}

			voicePrefix    = inputEl.value.trim();
			voiceCommitted = '';
			speechRecognition = new SpeechRecognitionCtor();
			speechRecognition.continuous      = false;
			speechRecognition.interimResults  = true;
			speechRecognition.lang            = 'en-US';
			speechRecognition.maxAlternatives = 1;

			listening = true;
			updateMicButton();

			speechRecognition.onstart = function onVoiceStart() {
				micBtn.innerHTML = '<i class="fas fa-spinner fa-spin" aria-hidden="true"></i>';
			};

			speechRecognition.onresult = function onVoiceResult(event) {
				let interim = '';
				let finalText = '';
				let i;

				for (i = event.resultIndex; i < event.results.length; i++) {
					if (event.results[i].isFinal) {
						finalText += event.results[i][0].transcript;
					} else {
						interim += event.results[i][0].transcript;
					}
				}

				if (finalText) {
					finalText = finalText.trim();
					if (finalText) {
						voiceCommitted = voiceCommitted
							? voiceCommitted + ' ' + finalText
							: finalText;
					}
				}

				inputEl.value = buildVoiceInputValue(interim.trim());
				autoGrow();
			};

			speechRecognition.onerror = function onVoiceError() {
				stopVoiceInput();
				showToast('Could not capture voice. Check your microphone.');
				if (Adzuki && typeof Adzuki.StatusMessage === 'function') {
					Adzuki.StatusMessage(
						'error',
						'Ensure that your microphone is turned on and/or your device is not muted.',
						'Voice Input Error'
					);
				}
			};

			speechRecognition.onend = function onVoiceEnd() {
				let text = buildVoiceInputValue('');
				listening         = false;
				speechRecognition = null;
				resetMicIcon();
				updateMicButton();

				inputEl.value = text;
				autoGrow();
				inputEl.focus();
			};

			try {
				speechRecognition.start();
			} catch (startErr) {
				stopVoiceInput();
				showToast('Could not start voice input');
			}
		}

		function toggleVoiceInput() {
			if (listening) {
				stopVoiceInput();
				return;
			}
			startVoiceInput();
		}

		// ------------------------------------------------------------------
		// Attachments (pending / pre-send)
		// ------------------------------------------------------------------

		function isImageMime(mime) {
			return typeof mime === 'string' && mime.indexOf('image/') === 0;
		}

		function fileKindLabel(mime, filename) {
			if (isImageMime(mime)) { return 'IMG'; }
			// Prefer filename extension — it's more reliable than the server's mime for documents.
			let ext = '';
			if (filename && filename.indexOf('.') >= 0) {
				ext = filename.split('.').pop().toLowerCase();
			}
			if (ext === 'pdf') { return 'PDF'; }
			if (ext === 'docx') { return 'DOCX'; }
			if (ext === 'xlsx') { return 'XLSX'; }
			if (ext === 'csv') { return 'CSV'; }
			if (ext === 'txt') { return 'TXT'; }
			return 'FILE';
		}

		function renderAttachments() {
			if (!pendingAttachments.length) {
				attachmentsEl.hidden    = true;
				attachmentsEl.innerHTML = '';
				setSendMode('send');
				return;
			}
			attachmentsEl.hidden = false;
			let html = '';
			for (let i = 0; i < pendingAttachments.length; i++) {
				let a        = pendingAttachments[i];
				let key      = a.id ? ('id-' + a.id) : ('tmp-' + a.temp_id);
				let isImage  = isImageMime(a.mime_type) || (a.thumb_url && a.thumb_url.indexOf('data:image/') === 0);
				let thumb;
				if (isImage && a.thumb_url) {
					thumb = '<img src="' + a.thumb_url + '" alt="" />';
				} else {
					let label = fileKindLabel(a.mime_type, a.filename);
					thumb = '<span class="mi-chip-doc">' + SVG.fileDoc + '<span class="mi-chip-doc-label">' + label + '</span></span>';
				}
				let status = '';
				if (a.uploading) { status = '<span class="mi-chip-spinner"></span>'; }
				else if (a.error) { status = '<span class="mi-chip-error" title="' + escapeHtml(a.error) + '">!</span>'; }
				html += '<div class="mi-chip" data-mi-chip-key="' + key + '">'
					+ '<span class="mi-chip-thumb">' + thumb + status + '</span>'
					+ '<span class="mi-chip-name" title="' + escapeHtml(a.filename || '') + '">' + escapeHtml(a.filename || '') + '</span>'
					+ '<button type="button" class="mi-chip-remove" data-mi-chip-remove="' + key + '" title="Remove">×</button>'
					+ '</div>';
			}
			attachmentsEl.innerHTML = html;
			setSendMode('send');
		}

		function handleFilesSelected(fileList) {
			if (!fileList || !fileList.length) { return; }

			let cap = 5;
			for (let i = 0; i < fileList.length; i++) {
				if (pendingAttachments.length >= cap) {
					showToast('Up to 5 attachments per message');
					break;
				}
				startUpload(fileList[i]);
			}
		}

		function startUpload(file) {
			let tempId  = ++uploadCounter;
			let record  = {
				temp_id  : tempId,
				id       : null,
				filename : file.name,
				mime_type: file.type,
				thumb_url: null,
				uploading: true,
				error    : null
			};
			pendingAttachments.push(record);
			renderAttachments();

			// Images get an instant local-preview thumbnail via FileReader.
			// Documents show a file-type icon instead (no preview needed).
			if (isImageMime(file.type)) {
				let reader = new FileReader();
				reader.onload = function readLocalImage() {
					record.thumb_url = reader.result;
					renderAttachments();
				};
				reader.readAsDataURL(file);
			}

			let body = new FormData();
			body.append('file', file);

			fetch(url('/Ai/maxxIntelligenceUpload'), {
				method     : 'POST',
				credentials: 'same-origin',
				headers    : { 'X-Requested-With': 'XMLHttpRequest' },
				body       : body
			}).then(function parseUploadResponse(r) { return r.json(); })
			  .then(function applyUploadResult(data) {
				if (!data || data.error) {
					record.uploading = false;
					record.error     = (data && data.error) || 'Upload failed';
					renderAttachments();
					showToast(record.error);
					return;
				}
				record.id        = data.id;
				record.uploading = false;
				record.mime_type = data.mime_type || record.mime_type;
				renderAttachments();
			}).catch(function handleUploadFailure(err) {
				record.uploading = false;
				record.error     = (err && err.message) ? err.message : 'Upload failed';
				renderAttachments();
				showToast(record.error);
			});
		}

		function removeAttachment(key) {
			let idx = -1;
			for (let i = 0; i < pendingAttachments.length; i++) {
				let a     = pendingAttachments[i];
				let thisKey = a.id ? ('id-' + a.id) : ('tmp-' + a.temp_id);
				if (thisKey === key) { idx = i; break; }
			}
			if (idx < 0) { return; }

			let removed = pendingAttachments[idx];
			pendingAttachments.splice(idx, 1);
			renderAttachments();

			// If the upload already completed, tell the server to delete it.
			if (removed.id) {
				fetch(url('/Ai/maxxIntelligenceDeleteAttachment/' + encodeURIComponent(removed.id)), {
					method     : 'POST',
					credentials: 'same-origin',
					headers    : { 'X-Requested-With': 'XMLHttpRequest' }
				}).catch(function ignoreDeleteError() {
					// Best-effort cleanup; no user action required on failure.
				});
			}
		}

		function consumePendingAttachments() {
			let ids = [];
			for (let i = 0; i < pendingAttachments.length; i++) {
				let a = pendingAttachments[i];
				if (a.id && !a.uploading && !a.error) { ids.push(a.id); }
			}
			return ids;
		}

		function clearPendingAttachments() {
			pendingAttachments = [];
			renderAttachments();
		}

		function autoGrow() {
			inputEl.style.height = 'auto';
			inputEl.style.height = Math.min(inputEl.scrollHeight, 180) + 'px';
			if (!generating) { setSendMode('send'); }
		}

		function showToast(text) {
			toastText.textContent = text;
			toastEl.classList.add('is-show');
			clearTimeout(toastTimer);
			toastTimer = setTimeout(function hideToast() {
				toastEl.classList.remove('is-show');
			}, 1800);
		}

		// ------------------------------------------------------------------
		// Network: drawer history
		// ------------------------------------------------------------------

		function loadConversations() {
			// Use named path param `q:` — query strings are not parsed as separate
			// GET params in this framework; `?q=x` becomes part of the action name.
			let path = '/Ai/maxxIntelligenceConversations';
			let trimmedSearch = (searchQuery || '').trim();
			if (trimmedSearch) {
				path += '/q:' + encodeURIComponent(trimmedSearch);
			}

			return fetch(url(path), {
				credentials: 'same-origin',
				headers    : { 
					'X-Requested-With': 'XMLHttpRequest',
					Accept            : 'application/json' }
			})
			.then(function parseConversationsResponse(r) { return r.json(); })
			.then(function applyConversationsData(data) {
				conversations = (data && data.conversations) || [];
				renderHistory();
			})
			.catch(function handleConversationsLoadError() {
				conversations = [];
				renderHistory();
			});
		}

		function loadConversation(id) {
			cancelGeneration();

			return fetch(url('/Ai/maxxIntelligenceConversation/' + encodeURIComponent(id)), {
				credentials: 'same-origin',
				headers    : { 'X-Requested-With': 'XMLHttpRequest',
Accept            : 'application/json' }
			})
			.then(function parseConversationResponse(r) { return r.json(); })
			.then(function applyConversationData(data) {
				if (!data || data.error) {
					showToast('Failed to load conversation');
					return;
				}
				activeConversationId = data.id;
				messages             = (data.messages || []).map(function mapConversationMessage(m) {
					return { id         : m.id,
log_id     : m.log_id,
role       : m.role,
content    : m.content,
attachments: m.attachments || [] };
				});
				renderMessages();
				renderHistory();
			});
		}

		function deleteConversation(id) {
			return fetch(url('/Ai/maxxIntelligenceDeleteConversation/' + encodeURIComponent(id)), {
				method     : 'POST',
				credentials: 'same-origin',
				headers    : { 'X-Requested-With': 'XMLHttpRequest' }
			})
			.then(function parseDeleteResponse(r) { return r.json(); })
			.then(function applyDeleteResponse() {
				if (Number(activeConversationId) === Number(id)) {
					activeConversationId = null;
					messages             = [];
					renderMessages();
				}
				loadConversations();
			});
		}

		// ------------------------------------------------------------------
		// Network: streaming send
		// ------------------------------------------------------------------

		function buildHistoryPayload(pendingUserText) {
			let history = messages
				.filter(function filterNonStreamingMessages(m) { return !m.streaming; })
				.map(function mapHistoryMessage(m) { return { role   : m.role,
content: m.content }; });

			if (pendingUserText !== null && pendingUserText !== undefined) {
				history.push({ role   : 'user',
content: pendingUserText });
			}

			return history;
		}

		// Controller for the in-progress reveal. While `generating` is true, the
		// Stop button shortcuts the reveal to full text and ends the turn.
		let revealHandle = null;
		let revealAbort  = false;

		function cancelGeneration() {
			stopVoiceInput();
			revealAbort = true;

			if (abortCtrl) {
				abortCtrl.abort();
				abortCtrl = null;
			}

			if (revealHandle) {
				clearTimeout(revealHandle);
				revealHandle = null;
			}

			for (let i = 0; i < messages.length; i++) {
				if (messages[i].streaming) {
					messages[i].streaming = false;
					if (!messages[i].content) {
						messages[i].content = '[stopped]';
					}
				}
			}

			generating = false;
			renderMessages();
			setSendMode('send');
		}

		function handleSend(overrideText, replayAttachments) {
			if (generating) { return; }

			let text            = ((overrideText !== null && overrideText !== undefined) ? overrideText : inputEl.value).trim();
			let isReplay        = Array.isArray(replayAttachments);
			let attachIds       = [];
			let sentAttachments = [];
			let i;
			let replayed;
			let pending;

			if (isReplay) {
				for (i = 0; i < replayAttachments.length; i++) {
					replayed = replayAttachments[i];
					if (replayed && replayed.id) {
						attachIds.push(replayed.id);
						sentAttachments.push({
							id       : replayed.id,
							filename : replayed.filename,
							mime_type: replayed.mime_type,
							thumb_url: replayed.thumb_url
						});
					}
				}
			} else {
				attachIds = consumePendingAttachments();

				// Snapshot the attachments we're sending so we can show thumbnails
				// inline on the just-sent user message. Each entry carries the
				// locally-loaded data URL as a fast thumbnail until the conversation
				// is reloaded from the server (which will populate real URLs).
				for (i = 0; i < pendingAttachments.length; i++) {
					pending = pendingAttachments[i];
					if (pending.id && !pending.uploading && !pending.error) {
						sentAttachments.push({
							id       : pending.id,
							filename : pending.filename,
							mime_type: pending.mime_type,
							thumb_url: pending.thumb_url
						});
					}
				}
			}

			let hasAttachments = attachIds.length > 0;
			if (!text && !hasAttachments) { return; }

			let userMsg      = { id         : uid(),
				role       : 'user',
				content    : text,
				attachments: sentAttachments };
			let assistantMsg = { id       : uid(),
				role     : 'assistant',
				content  : '',
				streaming: true };

			messages.push(userMsg, assistantMsg);
			if (!isReplay) {
				inputEl.value = '';
				clearPendingAttachments();
				autoGrow();
			}
			renderMessages();

			generating  = true;
			revealAbort = false;
			setSendMode('stop');
			if (typeof AbortController === 'undefined') {
				abortCtrl = null;
			} else {
				abortCtrl = new AbortController();
			}

			let payload = {
				messages       : buildHistoryPayload(null),
				conversation_id: activeConversationId,
				attachments    : attachIds
			};

			fetch(url('/Ai/maxxIntelligenceStream'), {
				method     : 'POST',
				credentials: 'same-origin',
				signal     : abortCtrl ? abortCtrl.signal : undefined,
				headers    : {
					'Content-Type'    : 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
					Accept            : 'application/json'
				},
				body: JSON.stringify(payload)
			}).then(function parseServerResponse(res) {
				if (!res.ok) {
					return res.text().then(function rethrowErrorText(t) { throw new Error('HTTP ' + res.status + ': ' + t); });
				}
				return res.json();
			}).then(function applyServerResponse(data) {
				if (!data || data.error) {
					throw new Error((data && data.error) || 'unknown error');
				}

				if (data.conversation_id) { activeConversationId = data.conversation_id; }

				return revealContent(assistantMsg, data.content || '');
			}).then(function finishTurn() {
				if (!generating) { return; }

				assistantMsg.streaming = false;
				generating             = false;
				abortCtrl              = null;
				renderMessages();
				setSendMode('send');
				loadConversations();
			}).catch(function handleSendError(err) {
				if (!generating) { return; }

				assistantMsg.streaming = false;
				generating             = false;
				abortCtrl              = null;

				if (err && err.name === 'AbortError') {
					if (!assistantMsg.content) { assistantMsg.content = '[stopped]'; }
				} else {
					assistantMsg.content = assistantMsg.content || ('Error: ' + (err && err.message ? err.message : 'request failed'));
				}

				renderMessages();
				setSendMode('send');
			});
		}

		// Progressive reveal: write `fullText` into assistantMsg.content in
		// small chunks so the UI feels like it's being typed. Resolves once
		// all text is shown (or immediately if Stop was pressed).
		function revealContent(assistantMsg, fullText) {
			return new Promise(function runReveal(resolve) {
				if (!fullText) {
					assistantMsg.content = '';
					updateStreamingMessage(assistantMsg.id, '');
					resolve();
					return;
				}

				let i           = 0;
				let total       = fullText.length;
				// Roughly 60 chars per 100ms — snappy but readable.
				let chunkSize   = Math.max(2, Math.ceil(total / 400));
				let tickMs      = 15;

				let step = function step() {
					if (revealAbort) {
						assistantMsg.content = fullText;
						updateStreamingMessage(assistantMsg.id, fullText);
						revealHandle = null;
						resolve();
						return;
					}

					i += chunkSize;
					if (i >= total) {
						assistantMsg.content = fullText;
						updateStreamingMessage(assistantMsg.id, fullText);
						revealHandle = null;
						resolve();
						return;
					}

					assistantMsg.content = fullText.slice(0, i);
					updateStreamingMessage(assistantMsg.id, assistantMsg.content);
					revealHandle = setTimeout(step, tickMs);
				};

				revealHandle = setTimeout(step, tickMs);
			});
		}

		// ------------------------------------------------------------------
		// Per-message actions
		// ------------------------------------------------------------------

		function copyMsg(msgId) {
			let m = findMessage(msgId);
			if (!m) { return; }

			function copyViaExecCommand(text) {
				let ok = false;
				let listener = function onCopy(e) {
					e.clipboardData.setData('text/plain', text);
					e.preventDefault();
				};
				try {
					document.addEventListener('copy', listener);
					ok = document.execCommand('copy');
				} catch (e) {
					ok = false;
				} finally {
					document.removeEventListener('copy', listener);
				}
				return Boolean(ok);
			}

			// Use the legacy event-based path first; this is already battle-tested
			// across this app and works in insecure contexts too.
			if (copyViaExecCommand(m.content)) {
				showToast('Copied to clipboard');
				return;
			}

			// Clipboard API fallback for browsers where execCommand is disabled.
			if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
				navigator.clipboard.writeText(m.content).then(function onClipboardWriteSuccess() {
					showToast('Copied to clipboard');
				}).catch(function onClipboardWriteError() {
					window.prompt('Copy to clipboard: Cmd/Ctrl+C, Enter', m.content);
				});
				return;
			}

			window.prompt('Copy to clipboard: Cmd/Ctrl+C, Enter', m.content);
		}

		function saveMsg(msgId) {
			let m = findMessage(msgId);
			if (!m) { return; }

			let body = new FormData();
			body.append('snippet', m.content);

			fetch(url('/Ai/saveSnippet'), {
				method     : 'POST',
				credentials: 'same-origin',
				headers    : { 'X-Requested-With': 'XMLHttpRequest' },
				body       : body
			}).then(function parseSaveSnippetResponse(r) { return r.json(); })
			  .then(function handleSaveSnippetSuccess() { showToast('Saved to Snippets'); })
			  .catch(function handleSaveSnippetFailure() { showToast('Failed to save snippet'); });
		}

		function insertMsg(msgId) {
			if (!onInsertContent) { return; }
			let m = findMessage(msgId);
			if (!m) { return; }
			onInsertContent(m);
		}

		function regenerate(msgId) {
			let idx = -1;
			for (let i = 0; i < messages.length; i++) {
				if (idsMatch(messages[i].id, msgId)) { idx = i; break; }
			}
			if (idx < 1) { return; }
			let userMsg = messages[idx - 1];
			if (!userMsg || userMsg.role !== 'user') { return; }

			// Drop both the user+assistant turn, then replay the same user input
			// and attachments so image/document Q&A still has vision/context.
			messages = messages.slice(0, idx - 1);
			renderMessages();
			handleSend(userMsg.content, userMsg.attachments || []);
		}

		function sendFeedback(msgId, rating) {
			let assistantIdx = -1;
			for (let i = 0; i < messages.length; i++) {
				if (idsMatch(messages[i].id, msgId)) { assistantIdx = i; break; }
			}
			if (assistantIdx < 1) { return; }

			let userMsg      = messages[assistantIdx - 1];
			let assistantMsg = messages[assistantIdx];
			if (!userMsg || userMsg.role !== 'user') { return; }

			let body = new FormData();
			body.append('session_id', activeConversationId ? 'mi-' + activeConversationId : 'mi-new');
			body.append('question', userMsg.content);
			body.append('response', assistantMsg.content);
			body.append('endpoint', 'maxx-intelligence');
			body.append('explanation', rating === 'up' ? 'thumbs_up' : 'thumbs_down');

			fetch(url('/Ai/sendFeedback'), {
				method     : 'POST',
				credentials: 'same-origin',
				headers    : { 'X-Requested-With': 'XMLHttpRequest' },
				body       : body
			}).catch(function handleFeedbackSendError() {
				// Non-blocking telemetry; no user-visible failure needed.
			});

			showToast('Thanks for the feedback');

			// Visually mark the clicked button for this turn.
			let btn = messagesEl.querySelector('[data-mi-msg-id="' + msgId + '"] [data-mi-msg-action="' + rating + '"]');
			if (btn) {
				btn.classList.add(rating === 'up' ? 'is-on-up' : 'is-on-down');
			}
		}

		function idsMatch(a, b) {
			return String(a) === String(b);
		}

		function findMessage(id) {
			for (let i = 0; i < messages.length; i++) {
				if (idsMatch(messages[i].id, id)) { return messages[i]; }
			}
			return null;
		}

		// ------------------------------------------------------------------
		// Drawer / empty state controls
		// ------------------------------------------------------------------

		function startNew() {
			cancelGeneration();
			activeConversationId = null;
			messages             = [];
			inputEl.value        = '';
			clearPendingAttachments();
			autoGrow();
			renderMessages();
			renderHistory();
			closeDrawer();
			inputEl.focus();
		}

		function clearActive() {
			if (!activeConversationId) { return; }
			cancelGeneration();
			let id = activeConversationId;
			deleteConversation(id);
		}

		function openDrawer() { drawer.classList.add('is-open'); backdrop.classList.add('is-open'); loadConversations(); }
		function closeDrawer() { drawer.classList.remove('is-open'); backdrop.classList.remove('is-open'); }
		function toggleDrawer() {
			if (drawer.classList.contains('is-open')) {
				closeDrawer();
			} else {
				openDrawer();
			}
		}

		// ------------------------------------------------------------------
		// Events
		// ------------------------------------------------------------------

		inputEl.addEventListener('input', autoGrow);
		inputEl.addEventListener('keydown', function handleInputKeydown(e) {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				if (!generating) { handleSend(); }
			}
		});

		sendBtn.addEventListener('click', function handleSendButtonClick() {
			if (generating) {
				cancelGeneration();
			} else {
				handleSend();
			}
		});

		if (attachBtn && fileInputEl) {
			// Fire the native file picker immediately — the input is hidden
			// offscreen via CSS, not display:none, so .click() reliably opens
			// the dialog across browsers.
			attachBtn.addEventListener('click', function openFilePicker() {
				fileInputEl.click();
			});
			fileInputEl.addEventListener('change', function handleFileInputChange(e) {
				handleFilesSelected(e.target.files);
				// Reset so re-selecting the same file re-fires `change`.
				e.target.value = '';
			});
		}

		// Opt-in to Bootstrap tooltip styling on the attach button when BS is
		// available; native title attribute remains the fallback.
		if (attachBtn && window.jQuery && jQuery.fn && jQuery.fn.tooltip) {
			jQuery(attachBtn).tooltip({ container: 'body' });
		}

		if (micBtn) {
			micBtn.addEventListener('click', function handleMicButtonClick() {
				toggleVoiceInput();
			});
			if (window.jQuery && jQuery.fn && jQuery.fn.tooltip) {
				jQuery(micBtn).tooltip({ container: 'body' });
			}
		}

		if (attachmentsEl) {
			attachmentsEl.addEventListener('click', function handleAttachmentsClick(e) {
				let removeEl = e.target.closest('[data-mi-chip-remove]');
				if (removeEl) {
					removeAttachment(removeEl.getAttribute('data-mi-chip-remove'));
				}
			});
		}

		root.addEventListener('click', function handleRootClick(e) {
			let actionEl = e.target.closest('[data-mi-action]');
			if (actionEl) {
				let action = actionEl.dataset.miAction;
				if (action === 'new') { startNew(); }
				else if (action === 'toggle-drawer') { toggleDrawer(); }
				else if (action === 'close-drawer') { closeDrawer(); }
				else if (action === 'clear') { clearActive(); }
				return;
			}

			let promptEl = e.target.closest('[data-mi-prompt]');
			if (promptEl) {
				inputEl.value = promptEl.dataset.miPrompt;
				autoGrow();
				inputEl.focus();
				return;
			}

			let deleteEl = e.target.closest('[data-mi-delete-id]');
			if (deleteEl) {
				e.stopPropagation();
				deleteConversation(deleteEl.dataset.miDeleteId);
				return;
			}

			let convoEl = e.target.closest('[data-mi-convo-id]');
			if (convoEl) {
				loadConversation(convoEl.dataset.miConvoId);
				return;
			}

			let msgActionEl = e.target.closest('[data-mi-msg-action]');
			if (msgActionEl) {
				let act = msgActionEl.dataset.miMsgAction;
				let id  = msgActionEl.dataset.miMsgId;
				if (act === 'copy') { copyMsg(id); }
				else if (act === 'save') { saveMsg(id); }
				else if (act === 'regenerate') { regenerate(id); }
				else if (act === 'insert') { insertMsg(id); }
				else if (act === 'up') { sendFeedback(id, 'up'); }
				else if (act === 'down') { sendFeedback(id, 'down'); }
			}
		});

		backdrop.addEventListener('click', closeDrawer);

		searchEl.addEventListener('input', function handleSearchInput(e) {
			searchQuery = e.target.value;
			loadConversations();
		});

		// Initial state
		autoGrow();
		updateMicButton();
		renderMessages();
		loadConversations();

		function sendMessage(text) {
			inputEl.value = text;
			autoGrow();
			handleSend();
		}

		function destroy() {
			cancelGeneration();
		}

		return {
			newChat     : startNew,
			openHistory : openDrawer,
			closeHistory: closeDrawer,
			sendMessage : sendMessage,
			destroy     : destroy
		};
	}

	Adzuki.MaxxIntelligence = {
		init          : init,
		formatMarkdown: formatAssistantMarkdown
	};
}(jQuery, window.Adzuki = window.Adzuki || {}));
